import React, { useCallback, useMemo } from "react";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useCanManageWorkspaceBilling } from "@hooks/permissions/useCanManageWorkspaceBilling";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useSettings } from "@components/settings/SettingsProvider";

interface BlockedFeatureHelpers {
    openPlans: () => void;
    isTrialing: boolean;
    canManageBilling: boolean;
}

interface FeatureAccessProps {
    feature: PREMIUM_FEATURES;
    disallowTrials?: boolean;
    renderAllowed: () => React.ReactElement | null;
    renderBlocked: (
        helpers: BlockedFeatureHelpers,
    ) => React.ReactElement | null;
}

export const FeatureAccess: React.FC<FeatureAccessProps> = ({
    feature,
    disallowTrials,
    renderAllowed,
    renderBlocked,
}) => {
    const { isTrialing } = useBillingStatus();
    const { open } = useSettings();
    const { hasAccess, isFetched } = useAllowFeature(feature, {
        disallowTrials,
    });
    const canManageBilling = useCanManageWorkspaceBilling();
    const openPlans = useCallback(() => {
        open(["plans"]);
    }, [open]);

    const helpers: BlockedFeatureHelpers = useMemo(
        () => ({ openPlans, isTrialing, canManageBilling }),
        [openPlans, isTrialing, canManageBilling],
    );

    if (!isFetched) {
        return null;
    }

    return hasAccess ? renderAllowed() : renderBlocked(helpers);
};
